import { api, newApi } from './api'
import z from 'zod'
import { validateAxiosResponse } from '@/utils/requestValidation'

const GetInsuranceApiResponseSchema = z.object({
  insurance_label: z.string().nullable(),
  payment_period: z
    .enum([
      'monthly',
      'quarterly',
      'half-yearly',
      'yearly',
      'once',
      'non-contributory',
    ])
    .nullable(),
  gross_price: z.number().nullable(),
  overallRating: z.number().nullable().optional(),
  name: z.string().nullable(),
  policy_number: z.string().nullable(),
  amount_insured_unlimited: z.boolean(),
  amount_insured: z.number().nullable(),
  parameters_values: z
    .object({
      en: z.record(z.string().nullable()).optional(),
      de: z.record(z.string().nullable()).optional(),
    })
    .nullable(),
  yes_no_values: z.record(z.union([z.literal(0), z.literal(1)])).nullable(),
  carrier: z.object({
    name: z.string(),
    logo_url: z.string().nullable(),
    customer_satisfaction_rating: z.number().nullable(),
    response_time_rating: z.number().nullable(),
    claim_handling_rating: z.number().nullable(),
  }),
  location: z.object({
    name: z.string(),
  }),
  files: z.array(
    z.object({
      name: z.string(),
      display_name: z.string(),
      created_at: z.string(),
      /**
       * We shouldn't need this in the response.
       * FIXME: BE should return files that are visible to the user
       */
      visible_to_user: z.boolean(),
      category: z.string().nullable(),
    }),
  ),
})

export type GetInsuranceApiResponse = {
  insurance_label: string | null
  payment_period:
    | 'monthly'
    | 'quarterly'
    | 'half-yearly'
    | 'yearly'
    | 'once'
    | 'non-contributory'
    | null
  gross_price: number | null
  overallRating: number | null
  name: string | null
  policy_number: string | null
  amount_insured_unlimited: boolean
  amount_insured: number | null
  parameters_values: {
    en?: Record<string, string>
    de?: Record<string, string>
  } | null
  yes_no_values: Record<string, 0 | 1> | null
  carrier: {
    name: string
    logo_url: string | null
    customer_satisfaction_rating: number | null
    response_time_rating: number | null
    claim_handling_rating: number | null
  }
  location: {
    name: string
  }
  files: Array<{
    name: string
    display_name: string
    created_at: string
    visible_to_user: boolean
    category:
      | 'contract'
      | 'offer'
      | 'application'
      | 'invoice'
      | 'incident'
      | 'claim'
      | 'cover_note'
      | 'contribution_arrears'
      | 'other'
      | null
  }>
}

export type PostInsuranceApiResponse = {
  created: string[]
}

export type PostInsuranceApiPayload = {
  categoryId: string
  carrierId: string
  policyNumber?: string | null
  contractStatus?: string | null
}[]

export const getInsuranceById = async (id: string) =>
  validateAxiosResponse(
    GetInsuranceApiResponseSchema,
    newApi.get(`/insurance/${id}`),
  )

export const createMultipleInsurances = (data: PostInsuranceApiPayload) => {
  const created = api.post<PostInsuranceApiResponse>('/insurance', {
    items: data,
  })
  // we need to cast for now because api client transforms return type in interceptor
  return (created as unknown) as Promise<PostInsuranceApiResponse>
}

const UpdateInsuranceSchema = z.object({
  policyNumber: z.string().optional(),
  contractStatus: z.enum(['transfer-initiated']).optional(),
})

type UpdateInsuranceSchema = z.infer<typeof UpdateInsuranceSchema>

export const updateInsurance = async (
  id: string,
  data: UpdateInsuranceSchema,
) => api.put(`/user/insurance/${id}`, data)
